/**
 * Nội dung (Content) route family — Plans 7.2 decomposition.
 * All routes are direct children of _authenticated (authenticatedRoute).
 */
import { lazy } from "react";
import { createRoute, redirect } from "@tanstack/react-router";

import { authenticatedRoute } from "../__root.js";
import { withSuspense } from "../route-helpers.js";

// ── Lazy page imports ────────────────────────────────────────────────

const PostsPage = lazy(() => import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.PostsPage })));
const PostTopicsPage = lazy(() =>
  import("@/features/content/post-topics-page").then((mod) => ({ default: mod.PostTopicsPage })),
);
const GuidesPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.GuidesPage })),
);
const DailyPracticePage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.DailyPracticePage })),
);
const LittleHousePage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.LittleHousePage })),
);
const LittleHouseGuideCreatePage = lazy(() =>
  import("@/features/little-house-content/item-pages").then((mod) => ({ default: mod.LittleHouseGuideCreatePage })),
);
const LittleHouseGuideDetailPage = lazy(() =>
  import("@/features/little-house-content/item-pages").then((mod) => ({ default: mod.LittleHouseGuideDetailPage })),
);
const LittleHouseCaseVariantCreatePage = lazy(() =>
  import("@/features/little-house-content/item-pages").then((mod) => ({ default: mod.LittleHouseCaseVariantCreatePage })),
);
const LittleHouseCaseVariantDetailPage = lazy(() =>
  import("@/features/little-house-content/item-pages").then((mod) => ({ default: mod.LittleHouseCaseVariantDetailPage })),
);
const LittleHouseFaqCreatePage = lazy(() =>
  import("@/features/little-house-content/item-pages").then((mod) => ({ default: mod.LittleHouseFaqCreatePage })),
);
const LittleHouseFaqDetailPage = lazy(() =>
  import("@/features/little-house-content/item-pages").then((mod) => ({ default: mod.LittleHouseFaqDetailPage })),
);
const LifeReleasePage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.LifeReleasePage })),
);
const LifeReleaseGuideCreatePage = lazy(() =>
  import("@/features/life-release-content/item-pages").then((mod) => ({ default: mod.LifeReleaseGuideCreatePage })),
);
const LifeReleaseGuideDetailPage = lazy(() =>
  import("@/features/life-release-content/item-pages").then((mod) => ({ default: mod.LifeReleaseGuideDetailPage })),
);
const LifeReleaseVariantCreatePage = lazy(() =>
  import("@/features/life-release-content/item-pages").then((mod) => ({ default: mod.LifeReleaseVariantCreatePage })),
);
const LifeReleaseVariantDetailPage = lazy(() =>
  import("@/features/life-release-content/item-pages").then((mod) => ({ default: mod.LifeReleaseVariantDetailPage })),
);
const LifeReleaseFaqCreatePage = lazy(() =>
  import("@/features/life-release-content/item-pages").then((mod) => ({ default: mod.LifeReleaseFaqCreatePage })),
);
const LifeReleaseFaqDetailPage = lazy(() =>
  import("@/features/life-release-content/item-pages").then((mod) => ({ default: mod.LifeReleaseFaqDetailPage })),
);
const MediaLibraryPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.MediaLibraryPage })),
);
const MediaLibraryCreatePage = lazy(() =>
  import("@/features/media-library").then((mod) => ({ default: mod.MediaLibraryCreatePage })),
);
const MediaLibraryDetailPage = lazy(() =>
  import("@/features/media-library").then((mod) => ({ default: mod.MediaLibraryDetailPage })),
);
const DownloadsPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.DownloadsPage })),
);
const SutrasPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.SutrasPage })),
);
const ImageAssetsPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.ImageAssetsPage })),
);
const VideoAssetsPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.VideoAssetsPage })),
);
const DocumentAssetsPage = lazy(() =>
  import("@/features/workspaces/module-pages").then((mod) => ({ default: mod.DocumentAssetsPage })),
);
const SelfCultivationWorkspacePage = lazy(() =>
  import("@/features/self-cultivation").then((mod) => ({ default: mod.SelfCultivationWorkspacePage })),
);
const SelfCultivationGuideCreatePage = lazy(() =>
  import("@/features/self-cultivation").then((mod) => ({ default: mod.SelfCultivationGuideCreatePage })),
);
const SelfCultivationGuideDetailPage = lazy(() =>
  import("@/features/self-cultivation").then((mod) => ({ default: mod.SelfCultivationGuideDetailPage })),
);
const SelfCultivationFaqCreatePage = lazy(() =>
  import("@/features/self-cultivation").then((mod) => ({ default: mod.SelfCultivationFaqCreatePage })),
);
const SelfCultivationFaqDetailPage = lazy(() =>
  import("@/features/self-cultivation").then((mod) => ({ default: mod.SelfCultivationFaqDetailPage })),
);
const PracticeHomePracticeGuidePage = lazy(() =>
  import("@/features/practice-support-home-guide").then((mod) => ({ default: mod.PracticeHomePracticeGuidePage })),
);
const WisdomPage = lazy(() =>
  import("@/features/wisdom-baihoa").then((mod) => ({ default: mod.WisdomPage })),
);
const WisdomCreatePage = lazy(() =>
  import("@/features/wisdom-baihoa/create-page").then((mod) => ({ default: mod.WisdomCreatePage })),
);
const WisdomDetailPage = lazy(() =>
  import("@/features/wisdom-baihoa/detail-page").then((mod) => ({ default: mod.WisdomDetailPage })),
);

// ── Detail / Create page imports ─────────────────────────────────────

const PostCreatePage = lazy(() =>
  import("@/features/content/post-create-page").then((mod) => ({ default: mod.PostCreatePage })),
);
const PostDetailPage = lazy(() =>
  import("@/features/content/post-detail-page").then((mod) => ({ default: mod.PostDetailPage })),
);
const DailyPracticeGuideCreatePage = lazy(() =>
  import("@/features/daily-recitation/guide-pages").then((mod) => ({ default: mod.DailyPracticeGuideCreatePage })),
);
const DailyPracticeGuideDetailPage = lazy(() =>
  import("@/features/daily-recitation/guide-pages").then((mod) => ({ default: mod.DailyPracticeGuideDetailPage })),
);
const DailyPracticePresetCreatePage = lazy(() =>
  import("@/features/daily-recitation/preset-pages").then((mod) => ({ default: mod.DailyPracticePresetCreatePage })),
);
const DailyPracticePresetDetailPage = lazy(() =>
  import("@/features/daily-recitation/preset-pages").then((mod) => ({ default: mod.DailyPracticePresetDetailPage })),
);
const DailyPracticeFaqCreatePage = lazy(() =>
  import("@/features/daily-recitation/faq-pages").then((mod) => ({ default: mod.DailyPracticeFaqCreatePage })),
);
const DailyPracticeFaqDetailPage = lazy(() =>
  import("@/features/daily-recitation/faq-pages").then((mod) => ({ default: mod.DailyPracticeFaqDetailPage })),
);

const SutraDownloadCreatePage = lazy(() =>
  import("@/features/downloads/download-create-page").then((mod) => ({
    default: () => (
      <mod.DownloadCreatePage
        backHref="/noi-dung/kinh-sach"
        backLabel="Kinh sách"
        defaultCategory="REFERENCE"
        pageTitle="Thêm kinh sách"
        sectionTitle="Thông tin kinh sách"
        descriptionPlaceholder="Mô tả ngắn về kinh sách..."
        categoryLabel="Loại nội dung"
        lockCategory
        lockedCategoryLabel="Kinh sách"
      />
    ),
  })),
);
const SutraDownloadDetailPage = lazy(() =>
  import("@/features/downloads/download-detail-page").then((mod) => ({
    default: () => (
      <mod.DownloadDetailPage
        backHref="/noi-dung/kinh-sach"
        backLabel="Kinh sách"
        emptyStateLabel="kinh sách"
        sectionTitle="Thông tin kinh sách"
        descriptionPlaceholder="Mô tả ngắn về kinh sách..."
        categoryLabel="Loại nội dung"
        lockCategory
        lockedCategoryLabel="Kinh sách"
      />
    ),
  })),
);

// Base-route wrappers for Guides + Downloads (same component, different back-nav)
const HuongDanGuideCreatePage = lazy(() =>
  import("@/features/guides/guide-create-page").then((mod) => ({
    default: () => <mod.GuideCreatePage backHref="/noi-dung/huong-dan" backLabel="Hướng dẫn" />,
  })),
);
const HuongDanGuideDetailPage = lazy(() =>
  import("@/features/guides/guide-detail-page").then((mod) => ({
    default: () => <mod.GuideDetailPage backHref="/noi-dung/huong-dan" backLabel="Hướng dẫn" />,
  })),
);
const TaiLieuDownloadCreatePage = lazy(() =>
  import("@/features/downloads/download-create-page").then((mod) => ({
    default: () => <mod.DownloadCreatePage backHref="/noi-dung/tai-lieu" backLabel="Tài liệu" />,
  })),
);
const TaiLieuDownloadDetailPage = lazy(() =>
  import("@/features/downloads/download-detail-page").then((mod) => ({
    default: () => <mod.DownloadDetailPage backHref="/noi-dung/tai-lieu" backLabel="Tài liệu" />,
  })),
);

// ── Routes ───────────────────────────────────────────────────────────

const postsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/bai-viet",
  component: withSuspense(PostsPage),
});
const postsCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/bai-viet/tao-moi",
  component: withSuspense(PostCreatePage),
});
const postsDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/bai-viet/$publicId",
  component: withSuspense(PostDetailPage),
});
const postTopicsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/chu-de",
  component: withSuspense(PostTopicsPage),
});

const guidesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/huong-dan",
  component: withSuspense(GuidesPage),
});
const guidesCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/huong-dan/tao-moi",
  component: withSuspense(HuongDanGuideCreatePage),
});
const guidesDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/huong-dan/$publicId",
  component: withSuspense(HuongDanGuideDetailPage),
});

const dailyPracticeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap",
  component: withSuspense(DailyPracticePage),
});
const dailyPracticeCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap/tao-moi",
  component: withSuspense(DailyPracticeGuideCreatePage),
});
const dailyPracticePresetCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap/kich-ban/tao-moi",
  component: withSuspense(DailyPracticePresetCreatePage),
});
const dailyPracticePresetDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap/kich-ban/$publicId",
  component: withSuspense(DailyPracticePresetDetailPage),
});
const dailyPracticeFaqCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap/hoi-dap/tao-moi",
  component: withSuspense(DailyPracticeFaqCreatePage),
});
const dailyPracticeFaqDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap/hoi-dap/$publicId",
  component: withSuspense(DailyPracticeFaqDetailPage),
});
const dailyPracticeDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-bai-tap/$publicId",
  component: withSuspense(DailyPracticeGuideDetailPage),
});

const littleHouseRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho",
  component: withSuspense(LittleHousePage),
});
const littleHouseGuideCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/huong-dan/tao-moi",
  component: withSuspense(LittleHouseGuideCreatePage),
});
const littleHouseGuideDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/huong-dan/$guidePublicId",
  component: withSuspense(LittleHouseGuideDetailPage),
});
const littleHouseCaseVariantCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/bien-the/tao-moi",
  component: withSuspense(LittleHouseCaseVariantCreatePage),
});
const littleHouseCaseVariantDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/bien-the/$variantPublicId",
  component: withSuspense(LittleHouseCaseVariantDetailPage),
});
const littleHouseFaqCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/hoi-dap/tao-moi",
  component: withSuspense(LittleHouseFaqCreatePage),
});
const littleHouseFaqDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/hoi-dap/$faqPublicId",
  component: withSuspense(LittleHouseFaqDetailPage),
});
const littleHouseLegacyCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/ngoi-nha-nho/tao-moi",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirect pattern
    throw redirect({ to: "/noi-dung/ngoi-nha-nho/huong-dan/tao-moi" });
  },
});

const lifeReleaseRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh",
  component: withSuspense(LifeReleasePage),
});
const lifeReleaseGuideCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/huong-dan/tao-moi",
  component: withSuspense(LifeReleaseGuideCreatePage),
});
const lifeReleaseGuideDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/huong-dan/$guidePublicId",
  component: withSuspense(LifeReleaseGuideDetailPage),
});
const lifeReleaseVariantCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/bien-the/tao-moi",
  component: withSuspense(LifeReleaseVariantCreatePage),
});
const lifeReleaseVariantDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/bien-the/$variantPublicId",
  component: withSuspense(LifeReleaseVariantDetailPage),
});
const lifeReleaseFaqCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/hoi-dap/tao-moi",
  component: withSuspense(LifeReleaseFaqCreatePage),
});
const lifeReleaseFaqDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/hoi-dap/$faqPublicId",
  component: withSuspense(LifeReleaseFaqDetailPage),
});
const lifeReleaseLegacyCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/phong-sanh/tao-moi",
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirect pattern
    throw redirect({ to: "/noi-dung/phong-sanh/huong-dan/tao-moi" });
  },
});

const mediaLibraryRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/thu-vien-phap-mon",
  component: withSuspense(MediaLibraryPage),
});
const mediaLibraryCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/thu-vien-phap-mon/tao-moi",
  component: withSuspense(MediaLibraryCreatePage),
});
const mediaLibraryDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/thu-vien-phap-mon/$publicId",
  component: withSuspense(MediaLibraryDetailPage),
});

const downloadsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/tai-lieu",
  component: withSuspense(DownloadsPage),
});
const downloadsCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/tai-lieu/tao-moi",
  component: withSuspense(TaiLieuDownloadCreatePage),
});
const downloadsDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/tai-lieu/$publicId",
  component: withSuspense(TaiLieuDownloadDetailPage),
});

const sutrasRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-sach",
  component: withSuspense(SutrasPage),
});
const sutrasCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-sach/tao-moi",
  component: withSuspense(SutraDownloadCreatePage),
});
const sutrasDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-sach/$publicId",
  component: withSuspense(SutraDownloadDetailPage),
});

const mediaRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/media",
  component: withSuspense(ImageAssetsPage),
});
const imageAssetsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/anh",
  component: withSuspense(ImageAssetsPage),
});
const videoAssetsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/video",
  component: withSuspense(VideoAssetsPage),
});
const documentAssetsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/tep-tai-lieu",
  component: withSuspense(DocumentAssetsPage),
});

const practiceHomeGuideRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-van-tu-tu",
  component: withSuspense(SelfCultivationWorkspacePage),
});
const selfCultivationGuideCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-van-tu-tu/huong-dan/tao-moi",
  component: withSuspense(SelfCultivationGuideCreatePage),
});
const selfCultivationGuideDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-van-tu-tu/huong-dan/$guidePublicId",
  component: withSuspense(SelfCultivationGuideDetailPage),
});
const selfCultivationFaqCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-van-tu-tu/hoi-dap/tao-moi",
  component: withSuspense(SelfCultivationFaqCreatePage),
});
const selfCultivationFaqDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/kinh-van-tu-tu/hoi-dap/$faqPublicId",
  component: withSuspense(SelfCultivationFaqDetailPage),
});

// Legacy alias for old tu-tai-gia guide page kept only for bookmarked draft review
const practiceHomeGuideLegacyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/tu-tu-tai-gia",
  component: withSuspense(PracticeHomePracticeGuidePage),
});

// Bạch thoại Phật pháp
const wisdomRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/bach-thoai",
  component: withSuspense(WisdomPage),
});
const wisdomCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/bach-thoai/tao-moi",
  component: withSuspense(WisdomCreatePage),
});
const wisdomDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/noi-dung/bach-thoai/$publicId",
  component: withSuspense(WisdomDetailPage),
});

// Legacy alias for old bookmarked route
const wisdomLegacyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/tri-tue",
  component: withSuspense(WisdomPage),
});

export const noiDungRoutes = [
  postsRoute,
  postsCreateRoute,
  postsDetailRoute,
  postTopicsRoute,
  guidesRoute,
  guidesCreateRoute,
  guidesDetailRoute,
  dailyPracticeRoute,
  dailyPracticeCreateRoute,
  dailyPracticePresetCreateRoute,
  dailyPracticePresetDetailRoute,
  dailyPracticeFaqCreateRoute,
  dailyPracticeFaqDetailRoute,
  dailyPracticeDetailRoute,
  littleHouseRoute,
  littleHouseGuideCreateRoute,
  littleHouseGuideDetailRoute,
  littleHouseCaseVariantCreateRoute,
  littleHouseCaseVariantDetailRoute,
  littleHouseFaqCreateRoute,
  littleHouseFaqDetailRoute,
  littleHouseLegacyCreateRoute,
  lifeReleaseRoute,
  lifeReleaseGuideCreateRoute,
  lifeReleaseGuideDetailRoute,
  lifeReleaseVariantCreateRoute,
  lifeReleaseVariantDetailRoute,
  lifeReleaseFaqCreateRoute,
  lifeReleaseFaqDetailRoute,
  lifeReleaseLegacyCreateRoute,
  mediaLibraryRoute,
  mediaLibraryCreateRoute,
  mediaLibraryDetailRoute,
  downloadsRoute,
  downloadsCreateRoute,
  downloadsDetailRoute,
  sutrasRoute,
  sutrasCreateRoute,
  sutrasDetailRoute,
  mediaRoute,
  imageAssetsRoute,
  videoAssetsRoute,
  documentAssetsRoute,
  practiceHomeGuideRoute,
  selfCultivationGuideCreateRoute,
  selfCultivationGuideDetailRoute,
  selfCultivationFaqCreateRoute,
  selfCultivationFaqDetailRoute,
  practiceHomeGuideLegacyRoute,
  wisdomRoute,
  wisdomCreateRoute,
  wisdomDetailRoute,
  wisdomLegacyRoute,
];
